import type { EditRequest, FieldDiff } from '@/stores/adminEdits';

const SHEET_ID = import.meta.env.VITE_GOOGLE_SHEET_ID || '';
const API_KEY = import.meta.env.VITE_GOOGLE_API_KEY || '';
const APPS_SCRIPT_URL = import.meta.env.VITE_GOOGLE_APPS_SCRIPT_URL || '';

/**
 * Save an Edit Request to Google Sheet (Sheet 2)
 */
export async function saveEditRequestToSheet(req: EditRequest): Promise<boolean> {
  const rowData = {
    action: 'saveEditRequest',
    sheet: 'Sheet2',
    requestId: req.id,
    timestamp: req.timestamp,
    status: req.status,
    villageName: req.villageName,
    villageId: req.villageId,
    headName: req.headName,
    familyId: req.familyId,
    memberName: req.memberName,
    memberId: req.memberId,
    diffsSummary: req.diffs.map((d) => `${d.label}: ${d.oldVal || '-'} -> ${d.newVal || '-'}`).join(' | '),
    originalData: JSON.stringify(req.originalData),
    updatedData: JSON.stringify(req.updatedData),
    diffsJson: JSON.stringify(req.diffs),
    submitterName: req.submitterName,
    submitterPhone: req.submitterPhone,
    note: req.note || '',
  };

  // 1. Try Apps Script Web App (if configured)
  if (APPS_SCRIPT_URL && !APPS_SCRIPT_URL.includes('placeholder')) {
    try {
      await fetch(APPS_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify(rowData),
      });
      return true;
    } catch (err) {
      console.warn('Apps Script save failed, trying Netlify endpoint:', err);
    }
  }

  // 2. Fallback to Netlify function proxy if available
  try {
    const netlifyRes = await fetch('/.netlify/functions/edit-member', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(rowData),
    });
    if (netlifyRes.ok) {
      return true;
    }
  } catch (err) {
    // In local dev without netlify functions, this is normal
  }

  return false;
}

/**
 * Fetch all Edit Requests directly from Google Sheet (Sheet 2)
 */
export async function fetchEditRequestsFromSheet(): Promise<EditRequest[]> {
  try {
    const res = await fetch(
      `https://sheets.googleapis.com/v4/spreadsheets/${SHEET_ID}/values/Sheet2!A1:P500?key=${API_KEY}`
    );
    const data = await res.json();

    if (!data.values || data.values.length <= 1) {
      return [];
    }

    const [headers, ...rows] = data.values;

    return rows.map((row: string[], idx: number) => {
      let diffs: FieldDiff[] = [];
      try {
        if (row[13]) {
          diffs = JSON.parse(row[13]);
        }
      } catch {
        diffs = [];
      }

      let originalData = {};
      try {
        if (row[11]) originalData = JSON.parse(row[11]);
      } catch {}

      let updatedData = {};
      try {
        if (row[12]) updatedData = JSON.parse(row[12]);
      } catch {}

      return {
        id: row[0] || `sheet2-req-${idx}`,
        timestamp: row[1] || '',
        status: (row[2] as any) || 'PENDING',
        villageName: row[3] || '',
        villageId: row[4] || '',
        headName: row[5] || '',
        familyId: row[6] || '',
        memberName: row[7] || '',
        memberId: row[8] || '',
        diffs,
        originalData,
        updatedData,
        submitterName: row[9] || '',
        submitterPhone: row[10] || '',
        note: row[14] || '',
        reviewedAt: row[15] || undefined,
      };
    });
  } catch (err) {
    console.warn('Could not read Sheet2 directly from Google Sheets API:', err);
    return [];
  }
}

export function isAppsScriptConfigured(): boolean {
  return Boolean(APPS_SCRIPT_URL && !APPS_SCRIPT_URL.includes('placeholder'));
}

/**
 * Update request status in Google Sheet (Sheet 2) when Admin approves or rejects
 */
export async function updateRequestStatusInSheet(
  reqOrId: EditRequest | string,
  status: 'APPROVED' | 'REJECTED',
  reviewedNote?: string
): Promise<{ success: boolean; sheetUpdated: boolean }> {
  if (!isAppsScriptConfigured()) {
    console.warn('Google Apps Script URL is not configured or is a placeholder. Skipping Google Sheet update.');
    return { success: false, sheetUpdated: false };
  }

  try {
    const isReqObj = typeof reqOrId === 'object' && reqOrId !== null;
    const req = isReqObj ? (reqOrId as EditRequest) : null;
    const requestId = req ? req.id : (reqOrId as string);

    await fetch(APPS_SCRIPT_URL, {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify({
        action: 'updateStatus',
        sheet: 'Sheet2',
        requestId,
        status,
        reviewedAt: new Date().toLocaleString('gu-IN', {
          dateStyle: 'medium',
          timeStyle: 'short',
        }),
        reviewedNote: reviewedNote || '',
        memberId: req?.memberId || '',
        memberName: req?.memberName || '',
        villageName: req?.villageName || '',
        villageId: req?.villageId || '',
        familyId: req?.familyId || '',
        headName: req?.headName || '',
        originalData: req?.originalData || {},
        updatedData: req?.updatedData || {},
      }),
    });
    return { success: true, sheetUpdated: true };
  } catch (err) {
    console.warn('Failed to update status in Google Sheet:', err);
    return { success: false, sheetUpdated: false };
  }
}
